<script setup lang="ts">
// ================================
// LE REGISTRE DES ENTRÉES, LES BORNES ET LES CONTRÔLES (DEV-1913, A7, A18).
//
// Ce que le client doit vivre : pour chaque valeur qui a servi au calcul, il lit
// d'où elle vient, quand elle a été relevée, et si elle a été corrigée — par lui
// ou par notre défaut. Sur la ligne du raccordement, il lit ce que nous en avons
// fait. Sur la ligne de chaque classe de son dénivelé actuel, il lit si la valeur
// tient devant les appels mesurés.
//
// Aucune bannière (règle de Mathieu) : chaque fait vit DANS la donnée concernée.
// Les corrections se font dans « Ajuster l'étude », qui reste le seul endroit où
// l'on écrit.
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import {
    lireControlesDenivele,
    lireRaccordement,
} from "@modules/courtage/utils/optim-turpe-v2.bornes"
import {
    formaterValeurEntree,
    heuresCreusesProposees,
    libelleReserveEntree,
    lireRegistre,
    reservesParChamp,
} from "@modules/courtage/utils/optim-turpe-v2.registre"
import {
    classesDeLaFta,
    estC5,
    formatDateFr,
    formatNombreFr,
    formatPuissanceFr,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    LIBELLE_RACCORDEMENT_STATUT,
    LIBELLE_VERDICT_DENIVELE,
    LIBELLE_VERDICT_DENIVELE_RECOMMANDE,
    legendeDesClasses,
    libelleFormuleTarifaire,
    libelleOptionTarifaire,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import {
    lireCouvertureDuPic,
    lireDeniveleRecommande,
    lireEcartementRaccordement,
    lireReserveDeniveleRecommande,
    lireStatutStructure,
} from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import { lireStructureChoisie } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import { nombreEurosDepassement } from "@modules/courtage/utils/optim-turpe-v2.montant"

import {
    ancreDepuisChamp,
    peutCompleter,
    saisieNonServie,
} from "@modules/courtage/utils/optim-turpe-v2.completer"
import type { AncreAffiner } from "@modules/courtage/utils/optim-turpe-v2.completer"
import {
    NIVEAU_2,
    NIVEAU_3,
} from "@modules/courtage/utils/optim-turpe-v2.boutons"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            // Vague wording du 14/10/2026 (partie 4, W20 à W26) : « Valeurs
            // utilisées », « Source », « puissance maximale atteinte »,
            // « puissance à couvrir », sans « nous ».
            titre: "Valeurs utilisées",
            intro: "Chaque valeur avec sa source. Corrigez-en une dans « Modifier les hypothèses » : la correction est indiquée au rapport.",
            registreNonServi:
                "Cette étude ne conserve pas la source des valeurs. Elles restent modifiables dans « Modifier les hypothèses ».",
            colonnes: {
                champ: "Valeur",
                valeur: "Retenue",
                provenance: "Source",
                releve: "Relevée le",
            },
            nonDatee: "non datée",
            aConfirmer: "À vérifier",
            statut: {
                ecartee: "Non retenue pour le calcul",
                absente: "Absente",
            },
            surchargeable: "Non modifiable ici (valeur Enedis)",
            pesePas: "Sans effet sur cette étude",
            surcharge: "Saisie le {date} ({ancienne} remplacée)",
            surchargeSeule: "Saisie le {date}",
            raccordement: {
                titre: "Puissance de raccordement",
                statutNonServi:
                    "Cette étude ne dit pas si cette valeur a limité la recommandation.",
                pointe: "Puissance maximale atteinte : {valeur}",
                bornee: "La recommandation retenue touche cette limite.",
                lache: "Les puissances recommandées restent sous la puissance de raccordement.",
                // Série UX du témoin (15/10/2026, 2.3 point 3) : la pastille
                // grise devient une phrase simple.
                nonAtteinte:
                    "Limite de {valeur} appliquée, non atteinte par la recommandation.",
                inconnue:
                    "La puissance de raccordement de ce compteur n'est pas connue. La recommandation n'est pas limitée par le raccordement ; vous pouvez saisir la valeur.",
                // L2 (23/09) : la borne de raccordement est « surchargeable par
                // le client au besoin ». Le geste vit ICI, sur la ligne du
                // raccordement, et pas seulement replié dans « Affiner ».
                lever: "Autoriser une puissance supérieure au raccordement et relancer l'étude",
                leverHelp:
                    "La recommandation pourra dépasser la puissance de raccordement connue : le gestionnaire de réseau devra étudier la demande. Ce choix est indiqué au rapport.",
                leveeParLeClient:
                    "Puissance supérieure au raccordement autorisée à votre demande sur cette étude : la recommandation n'est pas limitée par le raccordement.",
                leveeDemandee:
                    "Autorisation demandée, non confirmée par cette étude : le statut de cette limite reste celui affiché ci-dessus.",
                completer: "Saisir cette valeur",
            },
            completer: "Compléter",
            denivele: {
                titre: "Puissances souscrites actuelles devant la puissance maximale atteinte",
                nonServi:
                    "Le contrôle des puissances souscrites actuelles n'est pas servi pour cette étude.",
                // DEV-2164 cas 7 : sur un C5, ce n'est pas un manque, c'est le segment.
                puissanceUnique:
                    "Puissance unique : pas de contrôle par poste.",
                classe: "Poste",
                souscrite: "Souscrite",
                pointe: "Puissance maximale atteinte",
                reference: "Puissance à couvrir",
                referenceAide:
                    "Puissance à couvrir : la plus haute puissance atteinte sur ce poste ou sur un poste qui le précède, car les puissances souscrites ne peuvent pas baisser des heures de pointe (PTE) aux heures creuses de saison basse (HCB).",
                plancherC4: "minimum du segment C4",
                ecart: "Écart",
                verdict: "Lecture",
                sansMesure:
                    "Aucune mesure sur ce poste : pas de lecture possible.",
            },
            // courtage#254 (DEV-2184, M7) : la structure PROPOSÉE devant les appels.
            deniveleRecommande: {
                titre: "Puissances souscrites recommandées devant la puissance maximale atteinte",
                // Sous « déjà optimale » ou un surcoût, rien n'est recommandé (DEV-2183).
                titreProposee:
                    "Option chiffrée devant la puissance maximale atteinte",
                souscrite: "Puissance souscrite",
                // Mode « ma propre structure » : le client l'a imposée.
                titreChoisie:
                    "Vos puissances devant la puissance maximale atteinte",
                souscriteChoisie: "Vos puissances",
                cmdps: "Dépassements estimés de la période",
                cmdpsNonChiffree: "non chiffrés",
            },
            // courtage#254 (DEV-2184, M10) : le levier d'un raccordement écarté.
            levier: "Levier : {levier}",
        },
    },
})

type Props = {
    etude: OptimTurpeV2Etude
    /**
     * Rendu DANS la ligne « Ce qui a servi au calcul » (maquette allégée du
     * 07/10/2026) : sans cadre ni titre (l'en-tête de la ligne le porte), et
     * sans les deux tableaux « puissances souscrites devant les appels
     * mesurés », fondus dans « Pourquoi ces puissances » (temps 2).
     */
    integre?: boolean
}
const props = withDefaults(defineProps<Props>(), { integre: false })

const emit = defineEmits<{
    completer: [ancre: AncreAffiner]
    leverRaccordement: []
}>()

const getRegistre = computed(() => lireRegistre(props.etude))
/**
 * La colonne « Relevée le » ne s'affiche que si une ligne au moins porte une
 * date (recette du 28/09 : une colonne entière de « — »). Une ligne sans date,
 * à côté d'une autre datée, se dit « non datée ».
 */
const getAvecReleve = computed(
    () => getRegistre.value?.some((entree) => Boolean(entree.releveLe)) ?? false
)
const getReservesParChamp = computed(() =>
    reservesParChamp(props.etude.reserves)
)
const getRaccordement = computed(() => lireRaccordement(props.etude))
/**
 * Les lignes du contrôle, limitées aux classes que la formule du client porte :
 * une ligne « PM » servie sur un BTSUPCU4, qui n'a pas de pointe, ne décrit
 * aucune classe du client (revue du 25/09). Formule inconnue : rien n'est écarté.
 */
const getControles = computed(() => {
    const lignes = lireControlesDenivele(props.etude)
    if (!lignes) return null
    const classes = classesDeLaFta(
        props.etude.actuelle?.structure_tarifaire
            ?.formule_tarifaire_acheminement?.fta
    )
    const gardees = classes
        ? lignes.filter((ligne) => classes.includes(ligne.classe))
        : lignes
    return gardees.length > 0 ? gardees : null
})
/**
 * Le contrôle par classe est-il sans objet ? Le moteur le dit
 * (`controles.sans_objet`, courtage#249) ; le segment C5 reste le repli d'une
 * étude qui ne le porte pas.
 */
/**
 * La référence de chaque ligne est-elle servie (courtage#251, DEV-2171) ? Une
 * étude antérieure ne la porte pas : la colonne ne s'affiche pas.
 */
const getAvecReference = computed(
    () =>
        getControles.value?.some((ligne) => ligne.referenceMesuree !== null) ??
        false
)

/** « 94,8 kVA (HPB) » : la pointe de référence et ce qui la porte. */
function texteReference(ligne: {
    classe: string
    referenceMesuree: number | null
    referenceClasse: string | null
    unite: string
}): string {
    const valeur = formatPuissanceFr(ligne.referenceMesuree, ligne.unite)
    if (ligne.referenceMesuree === null || !ligne.referenceClasse) return valeur
    if (ligne.referenceClasse === "plancher-segment-c4") {
        return `${valeur} (${t("denivele.plancherC4")})`
    }
    return ligne.referenceClasse === ligne.classe
        ? valeur
        : `${valeur} (${ligne.referenceClasse})`
}

const getControleSansObjet = computed(
    () => Boolean(props.etude.controles?.sans_objet) || estC5(props.etude)
)
/** Le dénivelé PROPOSÉ devant les appels mesurés, classe par classe (M7). */
const getDeniveleRecommande = computed(() => {
    const lignes = lireDeniveleRecommande(props.etude)
    if (!lignes) return null
    const classes = classesDeLaFta(
        props.etude.meilleure?.structure_tarifaire
            ?.formule_tarifaire_acheminement?.fta
    )
    const gardees = classes
        ? lignes.filter((ligne) => classes.includes(ligne.classe))
        : lignes
    return gardees.length > 0 ? gardees : null
})
/** La colonne CMDPS ne s'affiche que si le moteur chiffre au moins une classe. */
const getAvecCmdpsParClasse = computed(
    () =>
        getDeniveleRecommande.value?.some((ligne) => ligne.cmdpsEur !== null) ??
        false
)
const getReserveDeniveleRecommande = computed(() =>
    lireReserveDeniveleRecommande(props.etude)
)
const getCouvertureDuPic = computed(() => lireCouvertureDuPic(props.etude))
const getStructureChoisie = computed(() => lireStructureChoisie(props.etude))
const getTitreDeniveleRecommande = computed(() =>
    getStructureChoisie.value
        ? t("deniveleRecommande.titreChoisie")
        : lireStatutStructure(props.etude) === "recommandee"
          ? t("deniveleRecommande.titre")
          : t("deniveleRecommande.titreProposee")
)
/** Ce qui fonde l'écartement du raccordement, son levier et la variante bornée (M10). */
const getEcartement = computed(() => lireEcartementRaccordement(props.etude))

/** « +1,5 kW » : l'écart signé de la souscrite proposée à l'appel mesuré. */
function texteEcart(ecart: number | null, unite: string): string {
    if (ecart === null) return "—"
    const signe = ecart > 0 ? "+" : ecart < 0 ? "−" : ""
    return `${signe}${formatPuissanceFr(Math.abs(ecart), unite)}`
}

function texteEuros(valeur: number | null): string {
    return valeur === null
        ? t("deniveleRecommande.cmdpsNonChiffree")
        : Math.abs(valeur) > 0 && Math.abs(valeur) < 0.5
          ? // Décision du pilote (14/10/2026) : jamais « 0 € » pour une
            // dépense de dépassement non nulle.
            `${nombreEurosDepassement(valeur)}\u00a0€`
          : `${formatNombreFr(valeur, 0)}\u00a0€`
}

const getHeuresCreuses = computed(() =>
    heuresCreusesProposees(props.etude.reserves)
)

/**
 * La borne est-elle levée sur CETTE étude ? Seul le moteur le dit, par le statut
 * `levee` qu'il sert. L'écho de la surcharge ENVOYÉE ne prouve rien de ce qui a
 * été ACCORDÉ : le confondre reviendrait à présenter « je l'ai demandé » comme
 * « c'est fait » (A18).
 */
const getBorneLevee = computed(() => getRaccordement.value?.statut === "levee")

/**
 * La levée a été demandée, mais le moteur ne la sert pas comme telle. On dit la
 * demande pour ce qu'elle est, sans affirmer l'effet et sans réoffrir le geste.
 */
const getLeveeDemandeeNonConfirmee = computed(
    () =>
        !getBorneLevee.value &&
        props.etude.inputs?.optimisation?.bornes?.raccordement_levee === true
)

/**
 * La limite est-elle appliquée SANS être atteinte ? Elle ne contraint alors
 * rien, et la lever ne changerait rien : on le dit, sans inviter au geste.
 */
const getAppliqueeNonAtteinte = computed(
    () =>
        getRaccordement.value?.statut === "appliquee" &&
        !getRaccordement.value.recommandationBornee
)

/** Le libellé du statut, qui distingue une limite qui contraint d'une limite non atteinte. */
const getStatutLibelle = computed(() => {
    const statut = getRaccordement.value?.statut
    if (!statut) return null
    return getAppliqueeNonAtteinte.value
        ? LIBELLE_RACCORDEMENT_STATUT.appliquee_non_atteinte
        : LIBELLE_RACCORDEMENT_STATUT[statut]
})

/**
 * Le geste de levée n'a de sens que sur une borne connue, non demandée, et qui
 * CONTRAINT la recommandation. Quand le statut n'est pas servi, on ne sait pas
 * si elle contraint : le geste reste offert.
 */
const getPeutLeverLaBorne = computed(() => {
    const borne = getRaccordement.value
    if (!borne || borne.valeur == null) return false
    if (getBorneLevee.value || getLeveeDemandeeNonConfirmee.value) return false
    if (borne.statut === null) return true
    return borne.statut === "appliquee" && borne.recommandationBornee
})

// R10 (revue du 08/10/2026) : l'unité du raccordement est celle du segment.
// Ni « unité présumée », ni réserve à confirmer, ni confirmation par le client
// ne s'écrivent plus à côté du raccordement.

/** Le raccordement est-il absent au point de mériter un geste de saisie (L29) ? */
const getRaccordementASaisir = computed(
    () =>
        getRaccordement.value === null ||
        getRaccordement.value.statut === "absente"
)

/**
 * Valeur d'entrée rendue lisible SANS être reformatée en un autre nombre : un
 * dénivelé est un objet, des plages horaires une liste, une marge une fraction.
 * Une absence reste une absence.
 */
function formatValeur(
    valeur: unknown,
    unite: string | null = null,
    champ: string | null = null
): string {
    // DEV-2218 : une option tarifaire ne s'imprime pas sous sa valeur
    // d'énumération (« HS_PTE_Fixe » ⇒ « pointe fixe »).
    // 05/10/2026 : la formule s'écrit avec son libellé devant son code.
    const lisible =
        champ === CHAMP_OPTION_TARIFAIRE
            ? libelleOptionTarifaire(valeur)
            : champ === CHAMP_FORMULE && typeof valeur === "string"
              ? libelleFormuleTarifaire(valeur)
              : valeur
    return formaterValeurEntree(lisible, unite, (n, d) => formatNombreFr(n, d))
}

const CHAMP_OPTION_TARIFAIRE =
    "structure_tarifaire.offre_tarifaire.option_tarifaire"
const CHAMP_FORMULE = "structure_tarifaire.formule_tarifaire_acheminement.fta"

/**
 * Les classes écrites en code dans le registre (les puissances par classe) :
 * le tableau porte leur légende (05/10/2026).
 */
const getLegendeRegistre = computed(() =>
    legendeDesClasses(
        (getRegistre.value ?? []).flatMap((entree) =>
            entree.valeur &&
            typeof entree.valeur === "object" &&
            !Array.isArray(entree.valeur)
                ? Object.keys(entree.valeur as Record<string, unknown>)
                : []
        )
    )
)
/** La légende des deux tableaux par classe : leurs classes et leurs références. */
const getLegendeControles = computed(() =>
    legendeDesClasses(
        (getControles.value ?? []).flatMap((ligne) => [
            ligne.classe,
            ...(ligne.referenceClasse ? [ligne.referenceClasse] : []),
        ])
    )
)
const getLegendeRecommande = computed(() =>
    legendeDesClasses(
        (getDeniveleRecommande.value ?? []).map((ligne) => ligne.classe)
    )
)

/**
 * « 108 kVA pour un appel de référence de 94,8 kVA (HPB) » : l'écart à la
 * référence dit en clair quand la classe est lue devant l'appel d'une autre
 * (DEV-2218). La ligne « HCB 108 · 8,06 · 1 240 % · Cohérent » se lisait comme
 * contradictoire : le verdict porte sur la référence, pas sur l'appel propre.
 */
function texteDevantLaReference(ligne: {
    classe: string
    souscrite: number | null
    referenceMesuree: number | null
    referenceClasse: string | null
    unite: string
}): string | null {
    if (
        ligne.souscrite === null ||
        ligne.referenceMesuree === null ||
        !ligne.referenceClasse ||
        ligne.referenceClasse === ligne.classe
    ) {
        return null
    }
    return `${formatPuissanceFr(ligne.souscrite, ligne.unite)} pour une puissance à couvrir de ${texteReference(ligne)}`
}
</script>

<template>
    <section
        class="optim-turpe-v2-registre flex flex-col gap-4"
        :class="
            integre
                ? ''
                : 'rounded-lg border border-surface-200 bg-white p-5 sm:p-6'
        "
        data-testid="optim-turpe-v2-registre"
    >
        <div v-if="!integre" class="flex flex-col gap-1">
            <p class="text-sm font-medium text-primary-800">{{ t("titre") }}</p>
            <p class="text-xs text-primary-500">{{ t("intro") }}</p>
        </div>

        <!-- LE REGISTRE (A18) : une ligne par entrée résolue. -->
        <div v-if="getRegistre" class="overflow-x-auto" data-legende-classes>
            <table class="w-full min-w-[34rem] text-left text-xs">
                <thead class="text-primary-500">
                    <tr>
                        <th class="py-1 pr-3 font-medium">
                            {{ t("colonnes.champ") }}
                        </th>
                        <th class="py-1 pr-3 font-medium">
                            {{ t("colonnes.valeur") }}
                        </th>
                        <th class="py-1 pr-3 font-medium">
                            {{ t("colonnes.provenance") }}
                        </th>
                        <th v-if="getAvecReleve" class="py-1 font-medium">
                            {{ t("colonnes.releve") }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="entree in getRegistre"
                        :key="entree.champ"
                        class="border-t border-surface-100 align-top"
                    >
                        <td class="py-1.5 pr-3 text-primary-800">
                            {{ entree.libelle }}
                            <span
                                v-if="!entree.surchargeable"
                                class="block text-primary-400"
                                >{{ t("surchargeable") }}</span
                            >
                            <span
                                v-else-if="!entree.peseSurLeResultat"
                                class="block text-primary-400"
                                >{{ t("pesePas") }}</span
                            >
                            <!-- L29 : une information absente se complète ICI,
                                 sur sa propre ligne. -->
                            <BaseButton
                                v-if="peutCompleter(entree)"
                                v-bind="NIVEAU_3"
                                :label="t('completer')"
                                icon="pi pi-pencil"
                                :data-testid="`optim-turpe-v2-completer-${entree.champ}`"
                                @click="
                                    emit(
                                        'completer',
                                        ancreDepuisChamp(entree.champ)!
                                    )
                                "
                            />
                            <!-- Une valeur que le client ne PEUT PAS corriger le
                                 dit, au lieu d'un bouton qui ne mène nulle part. -->
                            <span
                                v-else-if="saisieNonServie(entree.champ)"
                                class="block text-primary-400"
                                >{{ saisieNonServie(entree.champ) }}</span
                            >
                        </td>
                        <td class="py-1.5 pr-3 font-medium text-primary-900">
                            {{
                                formatValeur(
                                    entree.valeur,
                                    entree.unite,
                                    entree.champ
                                )
                            }}
                            <span
                                v-if="entree.statut !== 'retenue'"
                                class="block text-[var(--p-orange-700)]"
                                >{{ t(`statut.${entree.statut}`) }}</span
                            >
                        </td>
                        <td class="py-1.5 pr-3 text-primary-700">
                            <BaseTag
                                v-if="entree.estSurchargee"
                                severity="warn"
                                :value="
                                    entree.provenanceLibelle ??
                                    entree.provenance
                                "
                            />
                            <span v-else>{{
                                entree.provenanceLibelle ?? entree.provenance
                            }}</span>
                            <!-- Revue du support du 08/10 (A5) : la pastille
                                 « à confirmer » de l'en-tête se retrouve ici,
                                 sur sa ligne, en orange. -->
                            <BaseTag
                                v-if="entree.estAuDefaut"
                                severity="warn"
                                :value="t('aConfirmer')"
                                class="ml-1"
                                :data-testid="`optim-turpe-v2-a-confirmer-${entree.champ}`"
                            />
                            <!-- La surcharge est TRACÉE et MONTRÉE (A18). -->
                            <span
                                v-if="entree.surcharge?.date"
                                class="block text-primary-400"
                                >{{
                                    t(
                                        entree.surcharge.valeur_remplacee ==
                                            null
                                            ? "surchargeSeule"
                                            : "surcharge",
                                        {
                                            date:
                                                formatDateFr(
                                                    entree.surcharge.date
                                                ) ?? entree.surcharge.date,
                                            ancienne: formatValeur(
                                                entree.surcharge
                                                    .valeur_remplacee,
                                                entree.unite,
                                                entree.champ
                                            ),
                                        }
                                    )
                                }}</span
                            >
                        </td>
                        <td
                            v-if="getAvecReleve"
                            class="py-1.5 text-primary-600"
                            data-testid="optim-turpe-v2-registre-releve"
                        >
                            {{ formatDateFr(entree.releveLe) ?? t("nonDatee") }}
                        </td>
                    </tr>
                    <!-- Les réserves d'entrée se lisent SOUS la ligne qu'elles
                         qualifient, jamais dans une liste détachée. -->
                    <tr
                        v-for="entree in getRegistre"
                        v-show="getReservesParChamp[entree.champ]"
                        :key="`reserve-${entree.champ}`"
                        class="border-t border-surface-100"
                    >
                        <td
                            :colspan="getAvecReleve ? 4 : 3"
                            class="py-1 text-[var(--p-orange-700)]"
                        >
                            <span
                                v-for="reserve in getReservesParChamp[
                                    entree.champ
                                ] ?? []"
                                :key="reserve.code"
                                class="block"
                                >{{ libelleReserveEntree(reserve) }}</span
                            >
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <p
            v-if="getRegistre && getLegendeRegistre"
            class="text-xs text-primary-500"
            data-testid="optim-turpe-v2-registre-legende"
        >
            {{ getLegendeRegistre }}
        </p>
        <p v-else-if="!getRegistre" class="text-xs text-primary-500">
            {{ t("registreNonServi") }}
        </p>

        <!-- Heures creuses non publiées : le fait, et les plages proposées,
             servis par le moteur. Surchargeables dans « Affiner » (§3.4, A18). -->
        <p v-if="getHeuresCreuses" class="text-xs text-[var(--p-orange-700)]">
            {{ getHeuresCreuses.label }}
        </p>

        <!-- LE RACCORDEMENT (A7) : la valeur, son unité, et ce que nous en avons fait. -->
        <div class="flex flex-col gap-1 border-t border-surface-100 pt-3">
            <p class="text-xs font-medium text-primary-700">
                {{ t("raccordement.titre") }}
            </p>
            <template v-if="getRaccordement">
                <!-- 2.3 point 3 : appliquée sans être atteinte, la limite se
                     dit en une phrase ; ni pastille, ni ligne du pic, ni
                     phrase « restent sous la puissance de raccordement ». -->
                <p
                    v-if="getAppliqueeNonAtteinte"
                    class="text-sm text-primary-800"
                    data-testid="optim-turpe-v2-raccordement-non-atteint"
                >
                    {{
                        t("raccordement.nonAtteinte", {
                            valeur: formatPuissanceFr(
                                getRaccordement.valeur,
                                getRaccordement.unite
                            ),
                        })
                    }}
                </p>
                <p v-else class="text-sm text-primary-900">
                    <span class="font-semibold">{{
                        formatPuissanceFr(
                            getRaccordement.valeur,
                            getRaccordement.unite
                        )
                    }}</span>
                </p>
                <BaseTag
                    v-if="getStatutLibelle && !getAppliqueeNonAtteinte"
                    :severity="
                        getRaccordement.statut === 'appliquee'
                            ? 'success'
                            : getRaccordement.statut === 'absente'
                              ? 'secondary'
                              : 'warn'
                    "
                    :value="getStatutLibelle"
                    class="w-fit"
                />
                <p
                    v-else-if="!getStatutLibelle"
                    class="text-xs text-primary-500"
                >
                    {{ t("raccordement.statutNonServi") }}
                </p>
                <p
                    v-if="getRaccordement.statutMotifLibelle"
                    class="text-xs text-[var(--p-orange-700)]"
                >
                    {{ getRaccordement.statutMotifLibelle }}
                </p>
                <p
                    v-if="
                        getRaccordement.pointeMesureeMax !== null &&
                        !getAppliqueeNonAtteinte
                    "
                    class="text-xs text-primary-600"
                >
                    {{
                        t("raccordement.pointe", {
                            valeur: formatPuissanceFr(
                                getRaccordement.pointeMesureeMax,
                                getRaccordement.pointeMesureeUnite
                            ),
                        })
                    }}
                </p>
                <template v-if="getEcartement">
                    <p
                        v-if="getEcartement.pas"
                        class="text-xs text-primary-600"
                        data-testid="optim-turpe-v2-raccordement-pas"
                    >
                        {{ getEcartement.pas }}
                    </p>
                    <p
                        v-if="getEcartement.levier"
                        class="text-xs text-[var(--p-orange-700)]"
                        data-testid="optim-turpe-v2-raccordement-levier"
                    >
                        {{ t("levier", { levier: getEcartement.levier }) }}
                    </p>
                    <p
                        v-if="getEcartement.variante"
                        class="text-xs text-primary-600"
                        data-testid="optim-turpe-v2-raccordement-variante"
                    >
                        {{ getEcartement.variante }}
                    </p>
                </template>
                <p
                    v-if="getRaccordement.recommandationBornee"
                    class="text-xs text-[var(--p-orange-700)]"
                >
                    {{ t("raccordement.bornee") }}
                </p>
                <p
                    v-else-if="
                        getRaccordement.borneLache && !getAppliqueeNonAtteinte
                    "
                    class="text-xs text-primary-500"
                >
                    {{ t("raccordement.lache") }}
                </p>
            </template>
            <p v-else class="text-xs text-primary-500">
                {{ t("raccordement.inconnue") }}
            </p>

            <!-- A7 et L2 : la levée de la borne est un GESTE de la page, sur la
                 ligne du raccordement. Elle part comme surcharge tracée par le
                 moteur, et se relit ci-dessous sur l'étude qui en sort. -->
            <p
                v-if="getBorneLevee"
                class="text-xs text-[var(--p-orange-700)]"
                data-testid="optim-turpe-v2-borne-levee"
            >
                {{ t("raccordement.leveeParLeClient") }}
            </p>
            <p
                v-else-if="getLeveeDemandeeNonConfirmee"
                class="text-xs text-[var(--p-orange-700)]"
                data-testid="optim-turpe-v2-borne-levee-demandee"
            >
                {{ t("raccordement.leveeDemandee") }}
            </p>
            <template v-else-if="getPeutLeverLaBorne">
                <BaseButton
                    v-bind="NIVEAU_2"
                    :label="t('raccordement.lever')"
                    icon="pi pi-unlock"
                    class="w-fit"
                    data-testid="optim-turpe-v2-lever-raccordement"
                    @click="emit('leverRaccordement')"
                />
                <p class="text-xs text-primary-500">
                    {{ t("raccordement.leverHelp") }}
                </p>
            </template>
            <!-- Raccordement inconnu : le client peut le SAISIR (L29). -->
            <BaseButton
                v-else-if="getRaccordementASaisir"
                v-bind="NIVEAU_3"
                :label="t('raccordement.completer')"
                icon="pi pi-pencil"
                class="w-fit"
                data-testid="optim-turpe-v2-completer-raccordement"
                @click="emit('completer', 'raccordement')"
            />
        </div>

        <!-- LE CONTRÔLE DU DÉNIVELÉ ACTUEL (§4.5) : remplace l'état
             « sur-dimensionné », supprimé avec le plancher de 250 kW (A6). -->
        <div
            v-if="!integre"
            class="flex flex-col gap-1 border-t border-surface-100 pt-3"
        >
            <p class="text-xs font-medium text-primary-700">
                {{ t("denivele.titre") }}
            </p>
            <p v-if="getControleSansObjet" class="text-xs text-primary-500">
                {{ etude.controles?.libelle || t("denivele.puissanceUnique") }}
            </p>
            <div
                v-else-if="getControles"
                class="overflow-x-auto"
                data-legende-classes
            >
                <table class="w-full min-w-[30rem] text-left text-xs">
                    <thead class="text-primary-500">
                        <tr>
                            <th class="py-1 pr-3 font-medium">
                                {{ t("denivele.classe") }}
                            </th>
                            <th class="py-1 pr-3 font-medium">
                                {{ t("denivele.souscrite") }}
                            </th>
                            <th class="py-1 pr-3 font-medium">
                                {{ t("denivele.pointe") }}
                            </th>
                            <th
                                v-if="getAvecReference"
                                class="py-1 pr-3 font-medium"
                            >
                                {{ t("denivele.reference") }}
                            </th>
                            <th class="py-1 pr-3 font-medium">
                                {{ t("denivele.ecart") }}
                            </th>
                            <th class="py-1 font-medium">
                                {{ t("denivele.verdict") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="ligne in getControles"
                            :key="ligne.classe"
                            class="border-t border-surface-100"
                        >
                            <td class="py-1.5 pr-3 text-primary-800">
                                {{ ligne.classe }}
                            </td>
                            <td class="py-1.5 pr-3 text-primary-900">
                                {{
                                    formatPuissanceFr(
                                        ligne.souscrite,
                                        ligne.unite
                                    )
                                }}
                            </td>
                            <td class="py-1.5 pr-3 text-primary-900">
                                {{
                                    formatPuissanceFr(
                                        ligne.pointeMesuree,
                                        ligne.unite
                                    )
                                }}
                            </td>
                            <td
                                v-if="getAvecReference"
                                class="py-1.5 pr-3 text-primary-900"
                            >
                                {{ texteReference(ligne) }}
                            </td>
                            <td class="py-1.5 pr-3 text-primary-700">
                                {{
                                    ligne.ecartPct === null
                                        ? "—"
                                        : `${formatNombreFr(ligne.ecartPct, 1)} %`
                                }}
                            </td>
                            <td class="py-1.5 text-primary-700">
                                <span v-if="ligne.verdict">{{
                                    LIBELLE_VERDICT_DENIVELE[ligne.verdict]
                                }}</span>
                                <span v-else class="text-primary-400">{{
                                    t("denivele.sansMesure")
                                }}</span>
                                <span
                                    v-if="
                                        ligne.verdict &&
                                        texteDevantLaReference(ligne)
                                    "
                                    class="block text-primary-500"
                                    data-testid="optim-turpe-v2-denivele-devant-reference"
                                    >{{ texteDevantLaReference(ligne) }}</span
                                >
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p
                    v-if="getLegendeControles"
                    class="mt-1 text-xs text-primary-500"
                    data-testid="optim-turpe-v2-denivele-legende"
                >
                    {{ getLegendeControles }}
                </p>
                <p
                    v-if="getAvecReference"
                    class="mt-1 text-xs text-primary-500"
                >
                    {{ t("denivele.referenceAide") }}
                </p>
            </div>
            <p v-else class="text-xs text-primary-500">
                {{ t("denivele.nonServi") }}
            </p>
        </div>

        <!-- LE DÉNIVELÉ PROPOSÉ devant les appels mesurés (courtage#254, M7) :
             ce que la structure proposée laisse passer, à côté de ses valeurs. -->
        <div
            v-if="!integre && getDeniveleRecommande"
            class="flex flex-col gap-1 border-t border-surface-100 pt-3"
            data-testid="optim-turpe-v2-denivele-recommande"
        >
            <p class="text-xs font-medium text-primary-700">
                {{ getTitreDeniveleRecommande }}
            </p>
            <div class="overflow-x-auto" data-legende-classes>
                <table class="w-full min-w-[30rem] text-left text-xs">
                    <thead class="text-primary-500">
                        <tr>
                            <th class="py-1 pr-3 font-medium">
                                {{ t("denivele.classe") }}
                            </th>
                            <th class="py-1 pr-3 font-medium">
                                {{
                                    t(
                                        getStructureChoisie
                                            ? "deniveleRecommande.souscriteChoisie"
                                            : "deniveleRecommande.souscrite"
                                    )
                                }}
                            </th>
                            <th class="py-1 pr-3 font-medium">
                                {{ t("denivele.pointe") }}
                            </th>
                            <th class="py-1 pr-3 font-medium">
                                {{ t("denivele.ecart") }}
                            </th>
                            <th
                                v-if="getAvecCmdpsParClasse"
                                class="py-1 pr-3 font-medium"
                            >
                                {{ t("deniveleRecommande.cmdps") }}
                            </th>
                            <th class="py-1 font-medium">
                                {{ t("denivele.verdict") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="ligne in getDeniveleRecommande"
                            :key="ligne.classe"
                            class="border-t border-surface-100"
                        >
                            <td class="py-1.5 pr-3 text-primary-800">
                                {{ ligne.classe }}
                            </td>
                            <td class="py-1.5 pr-3 text-primary-900">
                                {{
                                    formatPuissanceFr(
                                        ligne.souscrite,
                                        ligne.unite
                                    )
                                }}
                            </td>
                            <td class="py-1.5 pr-3 text-primary-900">
                                {{
                                    formatPuissanceFr(
                                        ligne.pointeMesuree,
                                        ligne.unite
                                    )
                                }}
                            </td>
                            <td class="py-1.5 pr-3 text-primary-700">
                                {{ texteEcart(ligne.ecart, ligne.unite) }}
                            </td>
                            <td
                                v-if="getAvecCmdpsParClasse"
                                class="py-1.5 pr-3 text-primary-700"
                            >
                                {{ texteEuros(ligne.cmdpsEur) }}
                            </td>
                            <td class="py-1.5 text-primary-700">
                                <span v-if="ligne.verdict">{{
                                    LIBELLE_VERDICT_DENIVELE_RECOMMANDE[
                                        ligne.verdict
                                    ]
                                }}</span>
                                <span v-else class="text-primary-400">{{
                                    t("denivele.sansMesure")
                                }}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p
                v-if="getLegendeRecommande"
                class="text-xs text-primary-500"
                data-testid="optim-turpe-v2-denivele-recommande-legende"
            >
                {{ getLegendeRecommande }}
            </p>
            <p
                v-if="getReserveDeniveleRecommande"
                class="text-xs text-[var(--p-orange-700)]"
                data-testid="optim-turpe-v2-reserve-denivele-recommande"
            >
                {{ getReserveDeniveleRecommande }}
            </p>
            <p
                v-if="getCouvertureDuPic"
                class="text-xs text-primary-600"
                data-testid="optim-turpe-v2-couverture-du-pic"
            >
                {{ getCouvertureDuPic }}
            </p>
        </div>
    </section>
</template>
